import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LocalDBProvider } from './src/shared/data/LocalDB';
import { Animated, Image, useWindowDimensions, View } from 'react-native';
import InvisibleHeader from './src/specific/components/Main/InvisibleHeader';
import MenuCell from './src/shared/components/Menu/MenuCell';
import { NavigateFunction, BrowserRouter as Router, useLocation, useNavigate } from 'react-router-dom';
import AnimatedRoutes from './src/specific/utils/Navigation/AnimatedRoutes';
import User from './src/shared/data/user/User';
import Images, { bundleImage } from './src/specific/utils/Images';
import { APP_MENU_SCREENS } from './src/shared/components/Menu/Menu';
import { EventMenuSelectedChanged, EventUserAuthDB } from './src/shared/utils/Constants';
import { AppTarget, getAppTarget } from './src/specific/utils/Navigation/Host';
import DeepLink from './src/shared/services/DeepLink/DeepLink';
import { EventRegister } from './src/shared/utils/EventListeners';
import AppTheme from './src/shared/utils/Theme';
import { useAuthSession, useOptionalAuthSession } from './src/specific/services/Authentication/AuthProvider';
import { getDefaultMenuScreen, getSideMenuEntries, getSideMenuSelection, SubscriptionStatus } from './src/specific/components/Main/SideMenuState';

interface SideMenuProps {

}

const SideMenu = (props: SideMenuProps): React.JSX.Element | null => {

    const images: Images = new Images();
    const navigate: NavigateFunction = useNavigate();
    const { height } = useWindowDimensions();

    const session = useAuthSession();
    const [isMenuVisible, setIsMenuVisible] = useState<boolean>(false);
    const [isUserPremium, setIsUserPremium] = useState<boolean>(false);
    const [menuSelection, setMenuSelection] = useState<APP_MENU_SCREENS | null>(null);
    const [refresh, setRefresh] = useState<number>(0);

    let entries: APP_MENU_SCREENS[] = [];
    if (session.appUser !== null) {
        entries = ["ROOM", "SEQUENCES", "ACTIVITIES"];
        if (isUserPremium === false) {
            entries.push("PREMIUM");
        };
        entries.push("ACCOUNT");
    }

    useEffect(() => {
        EventRegister.addEventListener("newMenuSelection", (menuSelected: APP_MENU_SCREENS) => {
            onNewMenuSelected(menuSelected);
        });
        return () => {
            EventRegister.removeEventListener("newMenuSelection");
        };
    }, []);

    const selectDefaultMenu = () => {
        if (session.appUser !== null) {
            setMenuSelection("ROOM");
        }
    }

    useEffect(() => {
        if (session.status === "authenticated") {
            setIsMenuVisible(true);
            if (session.appUser !== null) {
                if (session.appUser?.subscriptionStatus === "premium") {
                    setIsUserPremium(true);
                } else {
                    setIsUserPremium(false);
                }
            } else {
                setIsUserPremium(false);
            }
        } else {
            setIsMenuVisible(false);
            setIsUserPremium(false);
        }
        if (menuSelection === null) {
            selectDefaultMenu();
        }
    }, [session]);

    const onNewMenuSelected = (selectedMenu: APP_MENU_SCREENS) => {
        const pathname: string = "/appMenu/" + selectedMenu.toLocaleLowerCase();
        const deepLink: DeepLink = DeepLink.getInstance();
        deepLink.path = pathname;
        navigate(pathname, { replace: true, state: { back: false } });
        setMenuSelection(selectedMenu);
    }

    const getMenuImageRessource = (): number | bundleImage => {
        if (session.appUser?.profile === "family") {
            return images.icLiliAtHomeCircle;
        } else if (session.appUser?.profile === "teacher") {
            return images.icLiliAtSchoolCircle;
        } else if (session.appUser?.profile === "animator") {
            return images.icLiliAtExtracurricularCircle;
        }
        return images.icLiliCircle
    }

    if (isMenuVisible === false) {
        return null;
    }

    const menuCells = entries.map(menuScreen => (
        <MenuCell
            key={menuScreen}
            width={120}
            menuScreenId={menuScreen}
            isSelected={menuSelection === menuScreen}
            onSelectedMenuChange={onNewMenuSelected}
        />
    ));

    return <View style={{ position: "fixed", width: 200, height, backgroundColor: "#ffffff", zIndex: 200, alignItems: "center", paddingVertical: 20 }}>
        <Image source={getMenuImageRessource()} style={{ width: 120, height: 120, marginBottom: 20 }} resizeMode="contain" />
        <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
            {menuCells}
        </View>
    </View>
}

const App = () => {

    const session = useAuthSession();
    const [refresh, setRefresh] = useState<number>(0);

    useEffect(() => {
        setRefresh(new Date().getTime());
    }, [session]);

    const paddingLeft = (): number => {
        if (session.status === "authenticated") {
            return 200;
        }
        return 0;
    }

    const renderSideMenu = (): React.JSX.Element | null => {
        const appTarget: AppTarget = getAppTarget();
        if (appTarget === "lhq") {
            return null;
        }
        if (session.status === "authenticated") {
            return <SideMenu />
        }
        return null;
    }

    return <LocalDBProvider>
        <View style={{ width: window.innerWidth, height: window.innerHeight }}>
            <Router>
                <View style={{ flex: 1 }}>
                    <View style={{ position: "sticky", zIndex: 100, top: 0, width: window.innerWidth }}>
                        <InvisibleHeader />
                    </View>
                    <View style={{ flex: 1, flexDirection: "row", width: window.innerWidth, height: window.innerHeight }}>
                        {renderSideMenu()}
                        <View style={{ paddingLeft: paddingLeft() }}>
                            <AnimatedRoutes />
                        </View>
                    </View>
                </View>
            </Router>
        </View>
    </LocalDBProvider>
}

export default App;
